// UsLocationHubPage — hub for one US location listing all 15 keyword pages.
// Route: /us/:location   e.g. /us/california

import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronRight, MapPin, Building2, Star, Quote, TrendingUp, CheckCircle2 } from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import CTABanner from "../components/homepage/CTABanner";
import NotFoundPage from "./NotFoundPage";
import { getLocationBySlug, citiesInState, US_LOCATIONS, isIndexable } from "../data/usLocations";
import { US_KEYWORDS } from "../data/usKeywords";

export default function UsLocationHubPage() {
  const { location: locSlug } = useParams();
  const loc = getLocationBySlug(locSlug);
  if (!loc) return <NotFoundPage />;

  const title = `${loc.name} Digital Marketing Agency — SEO, Ads, Ecommerce & More | Lumotis`;
  const desc = `Lumotis is a full-stack digital marketing agency serving ${loc.name} businesses. SEO, Google & Meta Ads, ecommerce, web design, email and AI search — all under one roof. Free audit.`;
  const pageUrl = `https://lumotis.com/us/${loc.slug}`;
  const locLabel = loc.type === "city" ? `${loc.name}, ${loc.stateAbbr}` : loc.name;
  const stateCities = loc.type === "state" ? citiesInState(loc.slug) : [];
  const parentState = loc.type === "city" && loc.stateSlug ? getLocationBySlug(loc.stateSlug) : null;
  const others = US_LOCATIONS.filter((l) => l.slug !== loc.slug).slice(0, 12);

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={pageUrl} />
        {!isIndexable(loc) && <meta name="robots" content="noindex, follow" />}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta name="geo.region" content={`US-${loc.stateAbbr}`} />
        <meta name="geo.placename" content={locLabel} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            name: `Lumotis — ${loc.name} Digital Marketing Agency`,
            url: pageUrl,
            areaServed: { "@type": loc.type === "city" ? "City" : "State", name: loc.name },
            parentOrganization: { "@type": "Organization", name: "Lumotis", url: "https://lumotis.com" },
          })}
        </script>
      </Helmet>

      <Navbar />

      <main data-testid={`us-hub-${loc.slug}`}>
        {/* HERO */}
        <section className="relative bg-white pt-28 md:pt-36 pb-16 md:pb-20 overflow-hidden border-b border-zinc-100">
          <div className="absolute inset-0 bg-grid-pattern opacity-60 pointer-events-none" />
          <div className="absolute right-0 top-0 w-2/3 h-2/3 bg-gradient-to-bl from-electric/8 via-transparent to-transparent pointer-events-none" />
          <div className="container relative">
            <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 flex-wrap mb-8">
              <Link to="/" className="hover:text-electric transition-colors">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/us" className="hover:text-electric transition-colors">USA</Link>
              <ChevronRight className="w-3 h-3" />
              {parentState && (
                <>
                  <Link to={`/us/${parentState.slug}`} className="hover:text-electric transition-colors">{parentState.name}</Link>
                  <ChevronRight className="w-3 h-3" />
                </>
              )}
              <span className="text-charcoal">{loc.name}</span>
            </nav>

            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2.5 border border-electric/25 bg-electric/8 rounded-full px-4 py-1.5 mb-6">
                  <span className="text-xl leading-none" aria-hidden>{loc.emoji || "🇺🇸"}</span>
                  <span className="text-overline text-electric">{locLabel}, USA</span>
                </div>
                <motion.h1
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="font-display text-4xl sm:text-5xl lg:text-[56px] font-extrabold leading-[1.05] tracking-tight text-balance text-charcoal"
                >
                  Digital Marketing Agency in {loc.name}
                </motion.h1>
                <p className="mt-4 font-display text-xl md:text-2xl font-bold leading-tight">
                  <span className="text-gradient">{loc.tagline}</span>
                </p>
                <p className="mt-6 text-lg text-zinc-500 leading-relaxed max-w-2xl">
                  Lumotis helps {loc.name} businesses grow revenue with SEO, Google & Meta Ads, ecommerce marketing, web design, email and AI search — every channel coordinated under one roof. {loc.insight}
                </p>
                <div className="mt-9 flex flex-wrap items-center gap-3">
                  <Link to="/seo-audit" className="inline-flex items-center gap-2 bg-electric text-white px-6 py-3.5 font-semibold rounded-xl hover:bg-electric-500 hover:-translate-y-0.5 transition-all shadow-lg shadow-electric/30">
                    Get a Free {loc.name} Audit <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/contact" className="inline-flex items-center gap-2 bg-white border border-zinc-200 text-charcoal px-6 py-3.5 font-semibold rounded-xl hover:border-electric hover:text-electric transition-colors">
                    Talk to a Strategist <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
              <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }} className="lg:col-span-5">
                <div className="rounded-2xl bg-charcoal text-white p-8 shadow-glow-brand">
                  <p className="text-overline text-electric mb-3">{loc.type === "city" ? "City market" : "State market"}</p>
                  <p className="font-display text-5xl font-extrabold leading-none">{loc.stat.value}</p>
                  <p className="mt-2 text-zinc-400 text-sm">{loc.stat.label}</p>
                  <p className="mt-6 text-xs uppercase tracking-widest text-zinc-500 mb-3">Key industries</p>
                  <div className="flex flex-wrap gap-2">
                    {loc.industries.map((ind) => (
                      <span key={ind} className="inline-flex items-center gap-1.5 border border-white/15 bg-white/5 rounded-full px-3 py-1 text-xs text-zinc-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-electric" /> {ind}
                      </span>
                    ))}
                  </div>
                  <p className="mt-6 text-xs uppercase tracking-widest text-zinc-500 mb-3">Areas we serve</p>
                  <p className="text-sm text-zinc-300">{loc.areas.join(" · ")}</p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* SERVICES GRID */}
        <section className="py-20 md:py-28 bg-zinc-50 border-b border-zinc-100">
          <div className="container">
            <div className="mb-12">
              <p className="text-overline text-electric mb-3">{US_KEYWORDS.length} services</p>
              <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
                Marketing services we offer in {loc.name}.
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {US_KEYWORDS.map((k, idx) => (
                <motion.div key={k.slug} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.3, delay: Math.min(idx * 0.03, 0.2) }}>
                  <Link
                    to={`/us/${loc.slug}/${k.slug}`}
                    className="group rounded-2xl border border-zinc-100 bg-white p-7 flex flex-col h-full shadow-card hover:shadow-card-hover hover:border-electric/30 hover:-translate-y-1 transition-all"
                    data-testid={`hub-kw-${k.slug}`}
                  >
                    <span className="text-3xl mb-4" aria-hidden>{k.emoji}</span>
                    <h3 className="font-display text-lg font-bold text-charcoal group-hover:text-electric leading-snug transition-colors">
                      {loc.name} {k.name}
                    </h3>
                    <p className="mt-2 text-sm text-zinc-500 leading-relaxed flex-1">{k.heroAccent}</p>
                    <span className="mt-5 inline-flex items-center gap-1 text-xs font-bold text-zinc-400 group-hover:text-electric uppercase tracking-widest transition-colors">
                      Explore <ArrowUpRight className="w-3.5 h-3.5" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* LOCAL PROOF (Tier-1 enriched content) */}
        {loc.enriched && (
          <section className="py-20 md:py-28 bg-white border-b border-zinc-100" data-testid="local-proof">
            <div className="container">
              <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
                <div className="lg:col-span-8">
                  <p className="text-overline text-electric mb-3">Local expertise</p>
                  <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
                    How marketing actually works in {loc.name}.
                  </h2>
                </div>
              </div>
              <div className="grid lg:grid-cols-12 gap-8">
                <div className="lg:col-span-7">
                  <p className="text-[17px] text-zinc-700 leading-relaxed">{loc.enriched.narrative}</p>
                  <ul className="mt-8 space-y-2.5">
                    {loc.enriched.challenges.map((c) => (
                      <li key={c} className="flex gap-3 text-[15px] text-zinc-700">
                        <CheckCircle2 className="w-5 h-5 text-electric shrink-0 mt-0.5" />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 grid grid-cols-3 gap-3">
                    {loc.enriched.facts.map((f) => (
                      <div key={f.label} className="rounded-2xl border border-zinc-100 bg-zinc-50 px-4 py-4">
                        <p className="font-display text-2xl font-extrabold text-charcoal">{f.value}</p>
                        <p className="text-[11px] uppercase tracking-widest text-zinc-500 mt-1 leading-snug">{f.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="lg:col-span-5 flex flex-col gap-5">
                  <motion.article initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45 }} className="rounded-2xl bg-charcoal text-white p-7 md:p-8 shadow-glow-brand">
                    <span className="inline-flex items-center gap-1.5 text-overline text-electric mb-4"><TrendingUp className="w-3.5 h-3.5" /> {loc.enriched.caseStudy.badge}</span>
                    <p className="font-display text-5xl md:text-6xl font-extrabold leading-none tracking-tight">{loc.enriched.caseStudy.metric}</p>
                    <p className="mt-2 text-zinc-400 text-xs uppercase tracking-widest">{loc.enriched.caseStudy.metricLabel}</p>
                    <h3 className="mt-6 font-display text-xl font-bold leading-snug">{loc.enriched.caseStudy.title}</h3>
                    <p className="mt-3 text-zinc-300 text-sm leading-relaxed">{loc.enriched.caseStudy.body}</p>
                  </motion.article>
                  <div className="rounded-2xl border border-zinc-100 bg-zinc-50 p-7 shadow-card">
                    <div className="flex items-center gap-0.5 mb-4">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className="w-4 h-4 fill-electric text-electric" />)}</div>
                    <Quote className="w-6 h-6 text-electric/40 mb-2" />
                    <blockquote className="font-display text-lg font-bold text-charcoal leading-snug">“{loc.enriched.testimonial.text}”</blockquote>
                    <p className="mt-4 text-sm text-zinc-500">— {loc.enriched.testimonial.name}, <span className="text-zinc-400">{loc.enriched.testimonial.role}</span></p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* CITIES IN STATE */}
        {stateCities.length > 0 && (
          <section className="py-20 md:py-24 bg-white border-b border-zinc-100">
            <div className="container">
              <p className="text-overline text-electric mb-3">Cities in {loc.name}</p>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold text-charcoal leading-[1.05] mb-8">
                Dedicated pages for {loc.name}'s biggest cities.
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {stateCities.map((c) => (
                  <Link key={c.slug} to={`/us/${c.slug}`} className="group rounded-2xl border border-zinc-100 bg-white p-5 shadow-card hover:shadow-card-hover hover:border-electric/30 transition-all">
                    <span className="block text-3xl leading-none mb-3 group-hover:scale-110 transition-transform origin-left" aria-hidden>{c.emoji || "🏙️"}</span>
                    <p className="font-display font-bold text-charcoal group-hover:text-electric transition-colors">{c.name}</p>
                    <p className="text-xs text-zinc-400 mt-1">{c.stat.value} · {c.stat.label.split("·")[0].trim()}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* OTHER LOCATIONS */}
        <section className="py-20 md:py-24 bg-white">
          <div className="container">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
              <div>
                <p className="text-overline text-electric mb-3">Other US markets</p>
                <h2 className="font-display text-3xl md:text-4xl font-extrabold text-charcoal leading-[1.05]">We also serve these locations.</h2>
              </div>
              <Link to="/us/locations" className="inline-flex items-center gap-2 border border-zinc-200 text-charcoal px-5 py-2.5 text-sm font-semibold rounded-xl hover:border-electric hover:text-electric transition-colors w-fit">
                All US locations <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {others.map((l) => (
                <Link key={l.slug} to={`/us/${l.slug}`} className="inline-flex items-center gap-1.5 border border-zinc-200 bg-white rounded-full px-4 py-2 text-sm font-semibold text-charcoal hover:border-electric hover:text-electric transition-colors">
                  <MapPin className="w-3.5 h-3.5 text-electric" /> {l.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <CTABanner />
      </main>
      <Footer />
    </>
  );
}
